/**
 * PrintDataForm — resolve a `print_data_needed` PRINT_FILE_ISSUE (Feature B).
 *
 * A dual product with no print_file_lookups row. A knowledgeable user enters the
 * FRONT + BACK design IDs (+ optional shape/variant per side); the backend
 * composes the print filenames, upserts the lookup row (the durable fix), writes
 * the Shopify metafields for sibling fan-out (best-effort), re-tests, and either
 * resolves (files present) or flips the issue to file_missing (artwork needs
 * RIP). Mirrors PrintFileSelfHealForm's submit→result state machine.
 *
 * Used by ResolveDialog for the print_data_needed case.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertCircle } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { DesignDataFormFooter, type DesignDataSubmitMode } from './DesignDataFormFooter';
import { toast } from 'sonner';
import { trpc } from '@/lib/trpc';
import { getUserMessage } from '@/lib/trpc-error-utils';

// Mirrors the backend allowlists (DESIGN_SHAPES + the Light/Dark/Medium variant
// set). "Unset" = leave the field blank (composes a no-suffix filename / SKU
// printId fallback) — there is NO "None" value.
const SHAPES = ['Rectangular', 'Square', 'Horizontal', 'Circle'] as const;
const VARIANTS = ['Light', 'Dark', 'Medium'] as const;
const UNSET = '__unset__';
const DESIGN_ID_RE = /^[A-Za-z0-9._-]+$/;

type Shape = (typeof SHAPES)[number];
type Variant = (typeof VARIANTS)[number];
type FormStatus = 'idle' | 'saving' | 'error';

interface PrintDataFormProps {
  issueId: string;
  blockedSku: string | null;
  onResolved: () => void;
  onSavedForUpload: () => void;
  onCancel: () => void;
}

export function PrintDataForm({ issueId, blockedSku, onResolved, onSavedForUpload, onCancel }: PrintDataFormProps) {
  const { t } = useTranslation('production');

  const [frontDesignId, setFrontDesignId] = useState('');
  const [backDesignId, setBackDesignId] = useState('');
  const [frontShape, setFrontShape] = useState<Shape | null>(null);
  const [backShape, setBackShape] = useState<Shape | null>(null);
  const [frontVariant, setFrontVariant] = useState<Variant | null>(null);
  const [backVariant, setBackVariant] = useState<Variant | null>(null);

  const [status, setStatus] = useState<FormStatus>('idle');
  // The button pressed for the save in flight (its spinner); read only while saving.
  const [pressedMode, setPressedMode] = useState<DesignDataSubmitMode>('save_for_upload');
  const [errorDetail, setErrorDetail] = useState<string | null>(null);

  const resolveMutation = trpc.printAgentAdmin.resolvePrintDataNeeded.useMutation();
  const isSaving = status === 'saving';

  const front = frontDesignId.trim();
  const back = backDesignId.trim();
  const frontInvalid = front.length > 0 && !DESIGN_ID_RE.test(front);
  const backInvalid = back.length > 0 && !DESIGN_ID_RE.test(back);
  // A print_data_needed issue is always a front+back dual — both sides required.
  const missingASide = front.length === 0 || back.length === 0;
  const submitDisabled = isSaving || missingASide || frontInvalid || backInvalid;

  const handleSubmit = async (mode: 'save_for_upload' | 'save_and_resolve' = 'save_for_upload') => {
    if (submitDisabled) return;
    setStatus('saving');
    setPressedMode(mode);
    setErrorDetail(null);
    try {
      const res = await resolveMutation.mutateAsync({
        issueId,
        mode,
        frontDesignId: front || null,
        backDesignId: back || null,
        frontShape,
        backShape,
        frontVariant,
        backVariant,
      });
      if (res.status === 'metadata_sync_failed') {
        setStatus('error');
        setErrorDetail(t('issues.printFiles.upload.metadataSyncFailed', { message: res.fanOut.error ?? '' }));
        return;
      }
      // `files_missing` = the lookup row is saved but the artwork isn't on the
      // cloud yet — same operator next step as `saved_for_upload`: upload it.
      if (res.status === 'saved_for_upload' || res.status === 'files_missing') {
        setStatus('idle');
        onSavedForUpload();
        return;
      }
      if (res.status === 'resolved') {
        const siblings = res.fanOut?.ok ? (res.fanOut.siblingVariantCount ?? 0) : 0;
        toast.success(
          siblings > 1
            ? t('issues.printFiles.printData.resolvedWithSiblings', { count: siblings })
            : t('issues.printFiles.printData.resolved'),
        );
        if (!res.fanOut?.ok && res.fanOut?.error) {
          // Resolve still succeeded; fan-out is a best-effort extra.
          toast.warning(t('issues.printFiles.printData.fanOutFailed', { message: res.fanOut.error }));
        }
        onResolved();
        return;
      }
      setStatus('idle');
    } catch (err) {
      setStatus('error');
      setErrorDetail(getUserMessage(err));
    }
  };

  return (
    <div className="space-y-4 py-2">
      {blockedSku && (
        <p className="text-base text-muted-foreground">
          {t('issues.printFiles.printData.skuContext')}{' '}
          <span className="font-mono text-foreground">{blockedSku}</span>
        </p>
      )}

      {/* Front / Back side blocks */}
      <div className="grid gap-4 sm:grid-cols-2">
        {([
          {
            side: 'front' as const,
            title: t('issues.printFiles.printData.frontTitle'),
            designId: frontDesignId,
            setDesignId: setFrontDesignId,
            invalid: frontInvalid,
            shape: frontShape,
            setShape: setFrontShape,
            variant: frontVariant,
            setVariant: setFrontVariant,
          },
          {
            side: 'back' as const,
            title: t('issues.printFiles.printData.backTitle'),
            designId: backDesignId,
            setDesignId: setBackDesignId,
            invalid: backInvalid,
            shape: backShape,
            setShape: setBackShape,
            variant: backVariant,
            setVariant: setBackVariant,
          },
        ]).map((s) => (
          <div key={s.side} className="space-y-3 rounded-lg border p-3">
            <p className="text-base font-semibold uppercase tracking-wide text-muted-foreground">{s.title}</p>
            <div className="space-y-1.5">
              <Label htmlFor={`pd-design-${s.side}`} className="text-base font-medium">
                {t('issues.printFiles.printData.designIdLabel')}
              </Label>
              <Input
                id={`pd-design-${s.side}`}
                value={s.designId}
                placeholder={s.side === 'front' ? 'MOTU8' : 'MOTU9'}
                className="h-12 font-mono"
                disabled={isSaving}
                aria-invalid={s.invalid || undefined}
                onChange={(e) => s.setDesignId(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor={`pd-shape-${s.side}`} className="text-base font-medium">
                {t('issues.printFiles.printData.shapeLabel')}
              </Label>
              <Select
                value={s.shape ?? UNSET}
                onValueChange={(v) => s.setShape(v === UNSET ? null : (v as Shape))}
                disabled={isSaving}
              >
                <SelectTrigger className="h-12" id={`pd-shape-${s.side}`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={UNSET}>{t('issues.printFiles.printData.unset')}</SelectItem>
                  {SHAPES.map((sh) => (
                    <SelectItem key={sh} value={sh}>
                      {sh}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor={`pd-variant-${s.side}`} className="text-base font-medium">
                {t('issues.printFiles.printData.variantLabel')}
              </Label>
              <Select
                value={s.variant ?? UNSET}
                onValueChange={(v) => s.setVariant(v === UNSET ? null : (v as Variant))}
                disabled={isSaving}
              >
                <SelectTrigger className="h-12" id={`pd-variant-${s.side}`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={UNSET}>{t('issues.printFiles.printData.unset')}</SelectItem>
                  {VARIANTS.map((va) => (
                    <SelectItem key={va} value={va}>
                      {va}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        ))}
      </div>

      {(frontInvalid || backInvalid) && (
        <p className="text-base text-destructive" role="alert">
          {t('issues.printFiles.printData.designIdInvalid')}
        </p>
      )}
      {missingASide && !frontInvalid && !backInvalid && (
        <p className="text-base text-muted-foreground">
          {t('issues.printFiles.printData.bothRequired')}
        </p>
      )}

      {status === 'error' && (
        <Alert variant="destructive" role="alert">
          <AlertCircle className="h-4 w-4" aria-hidden="true" />
          <AlertTitle>{t('issues.printFiles.printData.errorTitle')}</AlertTitle>
          <AlertDescription>
            {errorDetail || t('issues.printFiles.printData.errorGeneric')}
          </AlertDescription>
        </Alert>
      )}

      <DesignDataFormFooter
        savingMode={isSaving ? pressedMode : null}
        submitDisabled={submitDisabled}
        onCancel={onCancel}
        onSubmit={(mode) => void handleSubmit(mode)}
      />
    </div>
  );
}
